import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import LinearProgress from '@mui/material/LinearProgress';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VisibilityIcon from '@mui/icons-material/Visibility';

import Modal from '../../../../components/modules/Modal';

import { ContainedButton } from '../../../../components/modules/Buttons';
import CrossIcon from '../../../../assets/icons/cross.svg';
import styles from './ForgotPassword.module.scss';
import { restorePassword } from '../../../../api/usersApi';
import { isSafari } from '../../../../helpers';

type TRestorePasswordModal = {
  open: boolean;
  switchModal: () => void;
};
const isSafariBrowser = isSafari();

const RestorePasswordModal: React.FC<TRestorePasswordModal> = ({ open, switchModal }) => {
  const notification = useSnackbar();
  const location = useLocation();
  const [loading] = useState(false);
  const [creds, setCreds] = useState({
    password: '',
    password_repeat: '',
  });
  const searchParams = new URLSearchParams(location.search);
  const registerValue = searchParams.get('forgot_password_token');
  const restorePass = async (data: { password: string; password_repeat: string; forgot_password_token: string | null }) => {
    try {
      await restorePassword({
        password: data.password,
        password_repeat: data.password_repeat,
        forgot_password_token: registerValue,
      });
      notification.showMessage('An email was sent to your account');
      switchModal();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('Something went wrong');
      }
    }
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreds({
      ...creds,
      [e.target.name]: e.target.value,
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      password: creds.password,
      password_repeat: creds.password_repeat,
      forgot_password_token: registerValue,
    },
  });

  const [type, setType] = useState<'password' | 'text'>('password');
  const [repeatPasswordType, setRepeatPasswordType] = useState<'password' | 'text'>('password');

  const switchType = () => {
    if (type === 'password') {
      setType('text');
    } else {
      setType('password');
    }
  };

  const switchRepeatPasswordType = () => {
    if (repeatPasswordType === 'password') {
      setRepeatPasswordType('text');
    } else {
      setRepeatPasswordType('password');
    }
  };

  return (
    <Modal open={open} onClose={switchModal} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>Reset Password</div>
          <img src={CrossIcon} alt="close modal" onClick={switchModal} className={styles.closeIcon} />
        </div>
        <form
          className={styles.form}
          onSubmit={handleSubmit((data) => {
            restorePass(data);
          })}
        >
          <div style={{ position: 'relative' }}>
            <input
              {...register('password', {
                required: true,
                minLength: {
                  value: 8,
                  message: 'Password must contain at least 8 characters',
                },
                validate: (value) => value === creds.password_repeat || '',
              })}
              onChange={onChange}
              value={creds.password}
              className={errors.password ? styles.error : styles.field}
              name="password"
              placeholder="Password"
              type={type}
              required
            />
            {isSafariBrowser ? (
              <VisibilityIcon className={styles.eye} style={{ right: '-21px' }} onClick={switchType} />
            ) : (
              <div className={styles.eye} style={{ right: '10px' }} onClick={switchType}>
                {type === 'password' ? 'Show' : 'Hide'}
              </div>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <input
              {...register('password_repeat', {
                required: true,
                minLength: {
                  value: 8,
                  message: 'Password must contain at least 8 characters',
                },
                validate: (value) => value === creds.password || 'Passwords do not match.',
              })}
              onChange={onChange}
              className={errors.password_repeat ? styles.error : styles.field}
              value={creds.password_repeat}
              name="password_repeat"
              placeholder="Confirm password"
              type={repeatPasswordType}
              required
            />
            {isSafariBrowser ? (
              <VisibilityIcon className={styles.eye} style={{ right: '-21px' }} onClick={switchRepeatPasswordType} />
            ) : (
              <div className={styles.eye} style={{ right: '10px' }} onClick={switchRepeatPasswordType}>
                {repeatPasswordType === 'password' ? 'Show' : 'Hide'}
              </div>
            )}
          </div>
          {errors.password?.message && <p className={styles.red}>{errors.password?.message}</p>}
          {errors.password_repeat?.message && <p className={styles.red}>{errors.password_repeat?.message}</p>}
          <div className={styles.actionBtns}>
            <ContainedButton disabled={loading} type="submit">
              Save
            </ContainedButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RestorePasswordModal;
